import { useEffect, useRef, useState } from 'react';
import { workspacePath } from 'utils/workspace-route';
import type { JobKind } from 'utils/job-prefetch';
import { fetchTaskJobIds, fetchTaskReferencePhotos, ReferencePhoto } from './references-api';

export interface TaskModes {
    photos: ReferencePhoto[];
    taskJobIds: number[];
}

// the bar and the strip both need this: one request per task, shared
const cache = new Map<number, Promise<TaskModes>>();
// the bar remounts with every job: starting from the loaded value keeps the counts on screen
const loaded = new Map<number, TaskModes>();

export function loadTaskModes(taskId: number, force = false): Promise<TaskModes> {
    if (force || !cache.has(taskId)) {
        const promise = Promise.all([fetchTaskReferencePhotos(taskId), fetchTaskJobIds(taskId)])
            .then(([photos, taskJobIds]) => ({ photos, taskJobIds }));
        promise.then((value) => { if (cache.get(taskId) === promise) loaded.set(taskId, value); }, () => {
            if (cache.get(taskId) === promise) cache.delete(taskId);
        });
        cache.set(taskId, promise);
    }
    return cache.get(taskId)!;
}

// a change of ``reloadKey`` fetches again (a photo was deleted or restored)
export function useTaskModes(taskId: number, reloadKey?: unknown): TaskModes | null {
    const [modes, setModes] = useState<TaskModes | null>(() => loaded.get(taskId) ?? null);
    const first = useRef(true);
    useEffect(() => {
        let cancelled = false;
        loadTaskModes(taskId, !first.current).then((value) => { if (!cancelled) setModes(value); }).catch(() => {
            if (!cancelled) setModes({ photos: [], taskJobIds: [] });
        });
        first.current = false;
        return () => { cancelled = true; };
    }, [taskId, reloadKey]);
    return modes;
}

const lastJobKey = (taskId: number): string => `cvat-workspace-last-job-${taskId}`;
const lastFrameKey = (taskId: number): string => `cvat-workspace-last-frame-${taskId}`;

function readStorage(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

export function rememberTaskJob(taskId: number, jobId: number, frame?: number): void {
    try {
        window.sessionStorage.setItem(lastJobKey(taskId), String(jobId));
        if (typeof frame === 'number') window.sessionStorage.setItem(lastFrameKey(taskId), `${jobId}:${frame}`);
    } catch {
        // storage blocked: switching back opens the task's first job
    }
}

// the drawing to come back to; only for the job it was seen in, so it is always in that job's range
export function rememberedFrame(taskId: number, jobId: number): number | null {
    const [job, frame] = (readStorage(lastFrameKey(taskId)) ?? '').split(':').map(Number);
    return job === jobId && Number.isInteger(frame) && frame >= 0 ? frame : null;
}

export function drawingsTarget(modes: TaskModes, taskId: number): number | null {
    const remembered = Number(readStorage(lastJobKey(taskId)));
    if (modes.taskJobIds.includes(remembered)) return remembered;
    return modes.taskJobIds[0] ?? null;
}

// where a mode switch goes: the first reference photo, or back to the drawing that was open
export interface SwitchTarget {
    kind: JobKind;
    jobId: number;
    frame: number | null;
    path: string;
}

export function switchTarget(next: 'drawings' | 'references', modes: TaskModes, taskId: number): SwitchTarget | null {
    if (next === 'references') {
        const [first] = modes.photos;
        if (!first) return null;
        return {
            kind: 'reference',
            jobId: first.jobId,
            frame: first.frame,
            path: `${workspacePath(taskId, first.jobId, first.referenceId)}?frame=${first.frame}`,
        };
    }
    const jobId = drawingsTarget(modes, taskId);
    if (jobId === null) return null;
    const frame = rememberedFrame(taskId, jobId);
    const path = workspacePath(taskId, jobId);
    return {
        kind: 'task', jobId, frame, path: frame === null ? path : `${path}?frame=${frame}`,
    };
}
